import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";
import { normalizeTableNumber } from "@/lib/utils";
import { useSQLiteLocal } from "./useSQLiteLocal";

export interface OpenConsumptionItem {
  id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  notes: string | null;
  created_at: string;
  created_by: string | null;
  printed_at: string | null;
  source: string | null;
  operator_name: string | null;
}

export interface OpenConsumption {
  id: string;
  orderIds: string[];
  type: "table" | "command";
  number: string;
  customerName: string | null;
  items: OpenConsumptionItem[];
  subtotal: number;
  openedAt: string;
  ordersCount: number;
  isStale: boolean; // True if oldest order is >24h old
}

export function useOpenConsumption() {
  const { activeRestaurant } = useRestaurantContext();
  const [openTables, setOpenTables] = useState<OpenConsumption[]>([]);
  const [openCommands, setOpenCommands] = useState<OpenConsumption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isReady: sqliteReady, getAllActiveConsumptionsWithItems } = useSQLiteLocal();

  const fetchFromSQLiteCache = useCallback((): boolean => {
    if (!sqliteReady) return false;
    
    try {
      const consumptions = getAllActiveConsumptionsWithItems();
      if (consumptions.length === 0) return false;

      const now = new Date();
      const STALE_THRESHOLD_HOURS = 24;

      const tables: OpenConsumption[] = [];
      const commands: OpenConsumption[] = [];

      for (const c of consumptions) {
        const items: OpenConsumptionItem[] = c.items.map(item => ({
          id: item.id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          total_price: item.total_price,
          notes: item.notes || null,
          created_at: new Date().toISOString(),
          created_by: null,
          printed_at: null,
          source: null,
          operator_name: null,
        }));

        const subtotal = items.reduce((sum, i) => sum + i.total_price, 0);
        const openedAt = c.updatedAt || new Date().toISOString();
        const hoursSinceOpened = (now.getTime() - new Date(openedAt).getTime()) / (1000 * 60 * 60);

        const consumption: OpenConsumption = {
          id: c.id,
          orderIds: [c.orderId],
          type: c.type as "table" | "command",
          number: c.number,
          customerName: null,
          items,
          subtotal,
          openedAt,
          ordersCount: 1,
          isStale: hoursSinceOpened >= STALE_THRESHOLD_HOURS,
        };

        if (c.type === "table") {
          tables.push(consumption);
        } else {
          commands.push(consumption);
        }
      }

      setOpenTables(tables);
      setOpenCommands(commands);
      console.log(`[useOpenConsumption] Loaded from SQLite cache: ${tables.length} tables, ${commands.length} commands`);
      return true;
    } catch (err) {
      console.error("[useOpenConsumption] SQLite cache error:", err);
      return false;
    }
  }, [sqliteReady, getAllActiveConsumptionsWithItems]);

  const fetchOpenConsumption = useCallback(async () => {
    if (!activeRestaurant) {
      setIsLoading(false);
      return;
    }

    // Offline: use SQLite cache
    if (!navigator.onLine) {
      const loaded = fetchFromSQLiteCache();
      if (!loaded) {
        setOpenTables([]);
        setOpenCommands([]);
      }
      setIsLoading(false);
      return;
    }

    try {
      // Fetch open orders (pending or preparing status)
      const { data: orders, error } = await supabase
        .from("orders")
        .select(`
          id,
          order_type,
          table_number,
          tab_number,
          customer_name,
          subtotal,
          created_at,
          order_items (
            id,
            product_name,
            quantity,
            unit_price,
            total_price,
            notes,
            created_at,
            created_by,
            printed_at,
            source
          )
        `)
        .eq("restaurant_id", activeRestaurant.id)
        .in("status", ["pending", "preparing"])
        .order("created_at", { ascending: true });

      if (error) {
        // Network error: fallback to SQLite cache
        const loaded = fetchFromSQLiteCache();
        if (loaded) {
          toast.info("Usando dados do cache offline");
        } else {
          throw error;
        }
        setIsLoading(false);
        return;
      }

      // Group orders by table/command number (normalized to handle leading zeros)
      const tableGroups = new Map<string, typeof orders>();
      const commandGroups = new Map<string, typeof orders>();

      for (const order of orders || []) {
        if (order.order_type === "table" && order.table_number) {
          const normalizedNumber = normalizeTableNumber(order.table_number);
          const existing = tableGroups.get(normalizedNumber) || [];
          existing.push(order);
          tableGroups.set(normalizedNumber, existing);
        } else if (order.order_type === "tab" && order.tab_number) {
          const normalizedNumber = normalizeTableNumber(order.tab_number);
          const existing = commandGroups.get(normalizedNumber) || [];
          existing.push(order);
          commandGroups.set(normalizedNumber, existing);
        }
      }

      // Fetch operator names for all items
      const allOrderItems = (orders || []).flatMap(o => o.order_items || []);
      const userIds = [...new Set(allOrderItems.map(i => i.created_by).filter(Boolean))] as string[];
      
      let profilesMap: Record<string, string> = {};
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", userIds);
        
        if (profiles) {
          profilesMap = Object.fromEntries(profiles.map(p => [p.id, p.full_name || "Sem nome"]));
        }
      }

      const mapItems = (items: typeof allOrderItems): OpenConsumptionItem[] =>
        items.map(item => ({
          ...item,
          operator_name: item.source === "public"
            ? "Cliente"
            : item.created_by
              ? profilesMap[item.created_by] || "Operador"
              : null,
        }));

      // Convert grouped orders to OpenConsumption
      const now = new Date();
      const STALE_THRESHOLD_HOURS = 24;

      const tables: OpenConsumption[] = [];
      tableGroups.forEach((groupOrders, number) => {
        const allItems = mapItems(groupOrders.flatMap(o => o.order_items || []));
        const totalSubtotal = groupOrders.reduce((sum, o) => sum + o.subtotal, 0);
        const oldestOrder = groupOrders.reduce((oldest, o) => 
          new Date(o.created_at) < new Date(oldest.created_at) ? o : oldest
        );
        const customerNames = groupOrders.map(o => o.customer_name).filter(Boolean);
        const oldestDate = new Date(oldestOrder.created_at);
        const hoursSinceOpened = (now.getTime() - oldestDate.getTime()) / (1000 * 60 * 60);
        
        tables.push({
          id: `table-${number}`,
          orderIds: groupOrders.map(o => o.id),
          type: "table",
          number,
          customerName: customerNames[0] || null,
          items: allItems,
          subtotal: totalSubtotal,
          openedAt: oldestOrder.created_at,
          ordersCount: groupOrders.length,
          isStale: hoursSinceOpened >= STALE_THRESHOLD_HOURS,
        });
      });

      const commands: OpenConsumption[] = [];
      commandGroups.forEach((groupOrders, number) => {
        const allItems = mapItems(groupOrders.flatMap(o => o.order_items || []));
        const totalSubtotal = groupOrders.reduce((sum, o) => sum + o.subtotal, 0);
        const oldestOrder = groupOrders.reduce((oldest, o) => 
          new Date(o.created_at) < new Date(oldest.created_at) ? o : oldest
        );
        const customerNames = groupOrders.map(o => o.customer_name).filter(Boolean);
        const oldestDate = new Date(oldestOrder.created_at);
        const hoursSinceOpened = (now.getTime() - oldestDate.getTime()) / (1000 * 60 * 60);
        
        commands.push({
          id: `command-${number}`,
          orderIds: groupOrders.map(o => o.id),
          type: "command",
          number,
          customerName: customerNames[0] || null,
          items: allItems,
          subtotal: totalSubtotal,
          openedAt: oldestOrder.created_at,
          ordersCount: groupOrders.length,
          isStale: hoursSinceOpened >= STALE_THRESHOLD_HOURS,
        });
      });

      setOpenTables(tables);
      setOpenCommands(commands);
    } catch (error) {
      console.error("Error fetching open consumption:", error);
      // Last resort: try SQLite
      if (!fetchFromSQLiteCache()) {
        toast.error("Erro ao carregar consumos abertos");
      }
    } finally {
      setIsLoading(false);
    }
  }, [activeRestaurant, fetchFromSQLiteCache]);

  useEffect(() => {
    fetchOpenConsumption();
  }, [fetchOpenConsumption]);

  const cancelOrphanOrders = async (consumption: OpenConsumption) => {
    if (!activeRestaurant) {
      throw new Error("Nenhum restaurante ativo");
    }

    try {
      // Instrumentation: log every manual orphan cancel so we can confirm
      // whether it was triggered during diagnostic windows.
      try {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from('error_logs').insert([{
          restaurant_id: activeRestaurant.id,
          user_id: user?.id || null,
          source: 'manual_cancel_orphan',
          error_code: 'invoked',
          error_message: `Manual orphan cancel: ${consumption.type} ${consumption.number}`,
          context: {
            consumption_type: consumption.type,
            consumption_number: consumption.number,
            order_ids: consumption.orderIds,
            timestamp: new Date().toISOString(),
          } as any,
        }]);
      } catch {}

      // Update orders to cancelled
      const { error: orderError } = await supabase
        .from("orders")
        .update({ status: "cancelled" })
        .in("id", consumption.orderIds);

      if (orderError) throw orderError;

      // Ensure table/command is available (by number, not by current_order_id)
      if (consumption.type === "table") {
        await supabase
          .from("tables")
          .update({ status: "available", current_order_id: null })
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", consumption.number);
      } else {
        await supabase
          .from("commands")
          .update({ status: "available", current_order_id: null, customer_name: null })
          .eq("restaurant_id", activeRestaurant.id)
          .eq("number", consumption.number);
      }

      toast.success("Pedidos órfãos cancelados com sucesso");
      await fetchOpenConsumption();
    } catch (error) {
      console.error("Error cancelling orphan orders:", error);
      toast.error("Erro ao cancelar pedidos órfãos");
      throw error;
    }
  };

  return {
    openTables,
    openCommands,
    isLoading,
    refetch: fetchOpenConsumption,
    cancelOrphanOrders,
  };
}
